import type { StoryTourStep } from './storyTour';

/**
 * Mira's own words for each of the six coach-tour steps — Voice A ("chat with
 * a friend": first person, warm, a little wry), R217's applied/DAT-stamped
 * choice for every Mira-facing surface (docs/design/copy-voices-comparison-
 * 2026-07-05.html: "Ship Voice A on the Mira-facing surfaces... every
 * Mira-attributed note" — this tour narrates in Mira's own voice start to
 * finish, so it is squarely that surface, not a system one).
 *
 * The compass is the tester's own complaint (Jason's wife: "it looks
 * beautiful, but some of the language doesn't make sense"): if a word needs
 * explaining, it's the wrong word. Every line below describes only what the
 * app genuinely does today (NO INVENTION) — no line promises a feature this
 * build doesn't have.
 *
 * Kept as its own module (not inlined in CoachMark.tsx) so the exact copy is
 * reviewable/diffable in one place, mirrors quickStartGuide.ts's
 * QUICK_START_STEP_COPY split for the same reason.
 */
export interface StoryTourStepCopy {
  step: StoryTourStep;
  title: string;
  body: string;
  /** The primary button's label — varies per step since a couple of steps
   *  hand off to a real action rather than a plain "Next" (step 3 waits on
   *  the real tab tap; step 6 is a closing "Got it"). */
  primaryLabel: string;
}

export const STORY_TOUR_COPY: Record<StoryTourStep, StoryTourStepCopy> = {
  welcome: {
    step: 'welcome',
    title: "Hi, I'm Meira.",
    body:
      "Let's get you set up — about two minutes, six clicks, and I'll show you exactly where everything lives. Ready?",
    primaryLabel: 'Start',
  },
  'home-orientation': {
    step: 'home-orientation',
    title: "This is your story's health.",
    body: "It grows as you tell me more — right now there's nothing here yet, and that's exactly where everyone starts.",
    primaryLabel: 'Next',
  },
  'get-to-refine': {
    step: 'get-to-refine',
    title: "Let's go to Refine.",
    body: "That's where you and I actually talk. Tap Refine down there — or hit Next and I'll take you.",
    primaryLabel: 'Next',
  },
  // His 2026-07-20 walk: "we need the tour to get the resume and ask for it"
  // — the paste box gets its own coached stop BEFORE the first spoken story,
  // so the conversation that follows starts from something real.
  'paste-resume': {
    step: 'paste-resume',
    title: 'First, hand me your resume.',
    body:
      "Paste it into this box — resume, CV, or LinkedIn text all work. Or skip ahead and just tell me a story; either way we start from what's real.",
    primaryLabel: 'Next',
  },
  'first-story': {
    step: 'first-story',
    title: 'Tell me your first story.',
    body:
      "Tap the mic, then just talk — tell me about a moment at work you're proud of. Even a small one. Typing works too, if talking isn't your thing.",
    primaryLabel: 'Next',
  },
  'see-it-land': {
    step: 'see-it-land',
    title: 'And here it is.',
    body: 'That story lands right here on your canvas. Flip this switch and you\'ll see the same thing, laid out like a résumé.',
    primaryLabel: 'Next',
  },
  'where-to-next': {
    step: 'where-to-next',
    title: "When you're ready…",
    body:
      "I'll show you roles that actually fit what you just told me, over in Jobs and Get Found. No rush — come back anytime.",
    primaryLabel: 'Got it',
  },
};

/** The welcome card's secondary affordance — an explicit, equally-sized way
 *  out for someone who'd rather poke around first (mission: "[Start / I'll
 *  explore on my own]"). Every other step uses the shared [Skip tour] label
 *  (see CoachMark.tsx) — this one is worded for the FIRST decision point
 *  specifically, since "skip tour" reads oddly before the tour has shown
 *  anything yet. */
export const STORY_TOUR_WELCOME_SECONDARY = "I'll explore on my own";

export const STORY_TOUR_SKIP_LABEL = 'Skip tour';
